// React
import React, { useState } from 'react';

// Packages
import cc from 'classcat';
import t from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import AnimateHeight from 'react-animate-height';

// Utilities
import { useLayoutStore, useInitiativeDataStore } from 'utilities/store';
import { useLabels, useConfig } from 'utilities/hooks';

// Components
import * as DropdownCards from 'components/_assistedInteraction/dropdownCards';

// Icons
import { FiInfo, FiChevronUp, FiChevronDown } from 'react-icons/fi';

const DropDownComponent = ({ active }) => {
    // ///////////////////
    // STORES
    // ///////////////////

    const {
        mainNavigationWide,
        mainNavigationActive,
        secondaryNavigationActive,
    } = useLayoutStore();

    const { utilities } = useInitiativeDataStore();

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label } = useLabels();
    const { getAssistedInteractionsForDropdown } = useConfig({
        initiativeType:
            utilities.initiative.get()?.Configuration_Type__c || null,
    });

    // ///////////////////
    // DATA
    // ///////////////////

    const cardNames = getAssistedInteractionsForDropdown();

    // ///////////////////
    // STATE
    // ///////////////////

    const [expandedContent, setExpandedContent] = useState(false);

    // ///////////////////
    // RENDER
    // ///////////////////

    return (
        <AnimatePresence>
            {active && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{
                        y: 5,
                        opacity: 0,
                        transition: { duration: 0.2 },
                    }}
                    className={cc([
                        'fixed right-0 flex flex-col min-h-[100px] max-h-[80vh] overflow-auto justify-between p-16 mt-24 text-blue-100 bg-amber-20 shadow-md rounded-8 t-caption top-header page-mr page-ml z-logo print:hidden border-4 border-amber-200',
                        {
                            // No navigation is present
                            'left-0':
                                !mainNavigationActive && !mainNavigationWide,
                            // Narrow version of main navigation is present but no secondary navigation
                            'left-nav-narrow':
                                mainNavigationActive &&
                                !mainNavigationWide &&
                                !secondaryNavigationActive,
                            // Wide version of main navigation is present but no secondary navigation
                            'left-nav-wide':
                                mainNavigationActive &&
                                mainNavigationWide &&
                                !secondaryNavigationActive,
                            // Narrow version of main navigation is present and secondary navigation is present
                            'left-nav-combined ':
                                mainNavigationActive &&
                                !mainNavigationWide &&
                                secondaryNavigationActive,
                            // No wide version of main navigation + secondary navigation exists
                        },
                    ])}>
                    <div className="flex flex-col justify-between flex-grow gap-16 text-amber-120">
                        {/* Cards and interaction */}
                        {cardNames.map(cardName => {
                            const CardComponent =
                                DropdownCards[cardName] || null;
                            return (
                                <CardComponent
                                    key={cardName}
                                    promptKey={cardName}
                                    forceOpen={cardNames.length === 1}
                                />
                            );
                        })}
                        {/* About */}
                        <div className="flex flex-col px-16 py-8 transition-all select-none bg-amber-40 rounded-8 hover:bg-opacity-80">
                            <h4
                                className="relative flex items-center cursor-pointer t-h6 gap-x-8 top-2 "
                                onClick={() =>
                                    setExpandedContent(!expandedContent)
                                }>
                                <FiInfo className="relative -top-2 w-14 h-14" />{' '}
                                <span>{label('AI_PromptCard_About')}</span>
                                <span className="ml-auto -mt-3">
                                    {expandedContent ? (
                                        <FiChevronUp className="w-14 h-14" />
                                    ) : (
                                        <FiChevronDown className="w-14 h-14" />
                                    )}
                                </span>
                            </h4>
                            <AnimateHeight
                                duration={300}
                                height={expandedContent ? 'auto' : 0}>
                                <div className="grid grid-cols-6 gap-12 pb-8 mt-8 md:gap-24">
                                    <p className="col-span-6 md:col-span-3 t-caption">
                                        {label('AI_PromptCard_About_Col1')}
                                    </p>
                                    <p className="col-span-6 md:col-span-3">
                                        {label('AI_PromptCard_About_Col2')}
                                    </p>
                                    <div className="flex col-span-6 ml-auto space-x-32 t-caption-bold">
                                        <a
                                            href="https://www.foundgood.org/Foundgood_Privacy_Policy_-_June_3_2025.pdf"
                                            target="_blank"
                                            rel="noreferrer">
                                            {label('PrivacyPolicy')}
                                        </a>
                                        <a href="mailto:support@foundgood.org">
                                            support@foundgood.org
                                        </a>
                                    </div>
                                </div>
                            </AnimateHeight>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

DropDownComponent.propTypes = {
    active: t.bool,
};

DropDownComponent.defaultProps = {
    active: false,
};

export default DropDownComponent;
